import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import debounce from "lodash/debounce";
import { useParams, useLocation } from "react-router-dom";
import { useKeycloakToken } from '../common/useKeycloak';
import RouteDetails from "./RouteDetails";
import { addRouteDestinations, createRoute, deleteRoute, deleteRouteConfiguration, getRoute, getRouteApiKey, getRouteDetails, getRouteLog, getIntegrationDetails, getLogDetails, getReverseRoutes, getRouteConfiguration, getRoutesByIntegration, renameRoute, testConnectionAction, updateConnection, updateRouteConfiguration } from "../../api/routes/routesApi";
import { routeDisplayName } from "./routeDisplayName";
import { routeRecordId } from "./routeDisplayName";
import { FlowDirectionKey } from "./flowMap/flowMapTypes";
import { resolveRulesTarget } from "./flowMap/rulesTarget";
import TransformationRulesPanel from "./transformationRules/TransformationRulesPanel";
import {
    FieldErrors,
    FieldMapping,
    FieldMappingsConfig,
    STREAM_TYPES,
    StreamType,
    buildConfigWithRule,
    buildConfigWithoutRule,
    getRuleForDestination,
    parseServerErrors,
} from "./transformationRules/fieldMappingTypes";
import { Route, ConnectionConfiguration, RouteLog, Destination, DestinationType, Member, Organization, Provider, Source, buildRoute, buildDestination, buildMember, buildProvider } from "../common/ObjectTypes";
import { getDestinationsList } from "../../api/destinations/destinationsApi";
import { getSourcesForRoute } from "../../api/sources/sourcesApi";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import { loadAllOrganizations, loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from "../../api/apiUtils";
import { filterLogDate, filterLogStatus, filterLogType, logGlobalSearch, parseCursor, resetLogFilters } from "../activityLogs/ActivityLogsHelperFunctions";
import { parseDuration } from "../../utils/parseDuration";
import { changeDestinationGlobalSearch, destinationsNeedAuthEnrichment, enrichDestinationsWithAuth, filterDestinationOwner } from "../destinations/DestinationHelperFunctions";
import { changeSourceGlobalSearch } from "../sources/SourceHelperFunctions";
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import RouteDetailsSource from "./RouteDetailsSources";
import RouteDetailsLog from "./RouteDetailsLog";
import CancelChangesConfirmationModal from "./CancelChangesConfirmationModal";
import getUserPermissions from "../../api/users/getUserPermissions";
import { useTranslation } from "react-i18next";
import { useHelp, HelpCard } from "../../context/HelpContext";
import { getRouteKind, providerHasPushAction } from "../common/routeKind";
import { useKeycloak } from "@react-keycloak/web";
import { trackRouteEnabled, trackRouteDisabled, trackDestinationAdded } from '../../analytics/analytics';
import { getMyDetails } from "../../api/users/usersApi";
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";
import RouteProgressIndicator, { StepStatus } from "./NewRouteProgressIndicator/RouteProgressIndicator";
import { INREACH_TYPE } from "../common/configPanel/tempExemptions";
import { reverseRouteName } from "./hideReverseRoutes";
import { buttonClasses, inputClasses, skeletonClasses, routeDetailsClasses } from "../common/tailwindClassnames";
import SelectInput from "../common/SelectInput";
import NotFoundPage from "../common/NotFoundPage";
import { isValidRouteSubpage, routeTabPath } from "./routeSubpages";
import ConfigurationsTab from "./flowMap/ConfigurationsTab";
import AddDestinationDrawer from "./flowMap/AddDestinationDrawer";

const GENERAL_TAB_SKELETON_ROWS = [
    { label: "w-32", desc: "w-52" },
    { label: "w-24", desc: "w-44" },
];

export function ManageRouteDetails() {
    type FilterTags = {
        name: string;
        id: string;
    };
    type FilterTypes = {
        base_url__in: FilterTags[];
        enabled: FilterTags[];
        type__in: FilterTags[];
        owner__in: FilterTags[];
        ordering: string;
        search: string;
    };
    type SourceFilterTypes = {
        destination_url__in: FilterTags[];
        enabled: FilterTags[];
        type__in: FilterTags[];
        owner__in: FilterTags[];
        ordering: string;
        search: string;
    };
    type LogFilterTypes = {
        log_level: FilterTags[];
        log_type: FilterTags[];
        from_date: FilterTags[];
        to_date: FilterTags[];
        search: string;
        ordering: string;
    };

    const { t, i18n } = useTranslation();
    const { setHelpContent, closePanel: closeHelpPanel, setRightOffsetPx: setHelpOffsetPx } = useHelp();
    const { route_id, subpage } = useParams();
    // The :subpage route param accepts any string — an unknown value renders no
    // tab. Validate the param itself, not the resolved tab: an absent subpage is
    // valid and resolves below, once the Route's health is known.
    const isValidSubpage = isValidRouteSubpage(subpage);
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak, initialized } = useKeycloak();
    const location = useLocation();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [, setIsFromAddRouteFlow] = useState(false);
    const [canAddDestination, setCanAddDestination] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [route, setRoute] = useState<Route>(buildRoute());
    // A Route has exactly three addresses: /routes/:id, /routes/:id/sources and
    // /routes/:id/logs. The bare one IS the Configurations tab. An unhealthy
    // Route opens on Logs instead, but that happens once, as a redirect on
    // arrival (see below) — deriving it here on every render made Configurations
    // unreachable, because the tab strip sends Configurations back to the bare
    // URL and this would immediately re-answer "logs".
    const effectiveSubpage = subpage || "configurations";
    // The Route's own (editable) name — decoupled from the provider Connection's
    // name, seeded from the route record whenever the Route is (re)fetched.
    const [routeName, setRouteName] = useState("");
    // Some Routes come back with default_route: null even though they have a
    // route in routing_rules (seen on stage, e.g. ad1e98d7…). Everything that
    // PATCHes the route (destinations list, field mappings) needs the route id,
    // so fall back to the Route's first routing rule.
    const defaultRouteId: string | undefined = routeRecordId(route);
    const [routeHasDestinations, setRouteHasDestinations] = useState(() => route.destinations.length > 0);
    // Reported by the Configurations tab: accounts and flows whose required fields
    // are still empty. null until it has loaded them.
    const [pendingSetup, setPendingSetup] = useState<{ total: number, provider: boolean, destinations: boolean } | null>(null);
    const sourcesTabInitialized = useRef(false);
    const filterDataInitialized = useRef(false);
    // Reload requests are monotonic counters, not booleans (same pattern as the
    // logs fix in GUNDI-5576): a bump always registers as a state change, so a
    // request can never be swallowed by effect ordering or a stuck-true flag.
    // The pending refs carry the old boolean's retry semantics: a request that
    // couldn't run yet (no token) or failed stays pending and re-fires when the
    // token changes.
    const [routeReloadToken, setRouteReloadToken] = useState(0);
    const routeReloadPending = useRef(true);
    const requestRouteReload = useCallback(() => {
        routeReloadPending.current = true;
        setRouteReloadToken(previous => previous + 1);
    }, []);
    const [routeOnlyReloadToken, setRouteOnlyReloadToken] = useState(0);
    const routeOnlyReloadPending = useRef(false);
    const requestRouteOnlyReload = useCallback(() => {
        routeOnlyReloadPending.current = true;
        setRouteOnlyReloadToken(previous => previous + 1);
    }, []);
    const [routeProvider, setRouteProvider] = useState<Provider>(buildProvider());
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>({
        base_url__in: [],
        enabled: [],
        type__in: [],
        owner__in: [],
        ordering: "",
        search: ""
    });
    const [sourceFilter, setSourceFilter] = useState<SourceFilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('routeSourceFilter');
        return savedFilter ? JSON.parse(savedFilter) : {
            destination_url__in: [],
            enabled: [],
            type__in: [],
            owner__in: [],
            ordering: "",
            search: ""
        }
    });
    const [logsFilter, setLogsFilter] = useState<LogFilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('routeLogsFilter');

        if (savedFilter) {
            return JSON.parse(savedFilter);
        } else {
            const threeHoursAgo = new Date();
            threeHoursAgo.setHours(threeHoursAgo.getHours() - 3);

            const formattedDate = threeHoursAgo;

            return {
                log_type: [],
                log_level: [],
                from_date: [{
                    name: t("Start Date & Time"),
                    id: formattedDate.toISOString(),
                    attribute_name: "datetime"
                }],
                to_date: [],
                search: "",
                ordering: "-created_at"
            }
        }
    })
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    /* 
        ROUTE DESTINATIONS:
            - "destinations" array is for destinations already associated with the route
            - "existingDestPhase0" is for existing destinations that are available to add and unselected
            regardless of search/filter results
    */
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [existingDestPhase0, setExistingDestPhase0] = useState<Destination[]>([]);
    const [destinationsReloadToken, setDestinationsReloadToken] = useState(0);
    // Mirror of the reload counter, readable after an await: the drawer's "load
    // more" uses it to drop a response whose query has since been replaced. It is
    // bumped by the request itself, not during render — a response that resolves
    // before React commits the next render must already see the new generation.
    const destinationsReloadTokenRef = useRef(0);
    const destinationsReloadPending = useRef(false);
    const requestDestinationsReload = useCallback(() => {
        destinationsReloadPending.current = true;
        destinationsReloadTokenRef.current += 1;
        setDestinationsReloadToken(previous => previous + 1);
    }, []);

    // Transformation Rules side panel state
    const [rulesPanelOpen, setRulesPanelOpen] = useState(false);
    const [addDestinationDrawerOpen, setAddDestinationDrawerOpen] = useState(false);
    // The drawer refetches its list on open, but that effect runs after the first
    // paint — opening with the previous rows still in state flashed them for a
    // frame, and a click could land on a row about to be replaced by the fetch.
    // Opening through here shows the loading skeleton from the very first frame.
    const openAddDestinationDrawer = () => {
        setExistingDestPhase0([]);
        setUpdatingDestinations(true);
        setAddDestinationDrawerOpen(true);
    };
    // Page bookkeeping for the drawer's paginator — the same numbered pagination
    // the Routes list uses. The count is the API's, so it includes records
    // this Route already uses (they're filtered out of the rows shown).
    const [destinationsTotalCount, setDestinationsTotalCount] = useState(0);
    const [destinationsPageOffset, setDestinationsPageOffset] = useState(0);
    // Generation counter for page clicks, so the latest one wins regardless of the
    // order the responses come back in.
    const destinationsPageRequestRef = useRef(0);
    // Drawer workspace filter — defaults to the Route's workspace; clearing
    // it lists destinations from every workspace the user can see.
    const [drawerWorkspaceFilter, setDrawerWorkspaceFilter] = useState<{ label: string; value: string } | null>(null);
    const [rulesPanelDestination, setRulesPanelDestination] = useState<Destination | null>(null);
    // Which arrow the rules panel was opened from. "out" = this Route (provider →
    // site); "in" = the reverse Route (site → provider), whose field mappings live
    // on that other route record.
    const [rulesDirection, setRulesDirection] = useState<FlowDirectionKey>("out");
    // Field mappings of the reverse Route of each site, keyed by site id. The
    // reverse direction is a separate route record, so its configuration is a
    // separate fetch — without it the "in" arrow could only ever show "no rule".
    const [reverseConfigBySite, setReverseConfigBySite] = useState<Record<string, { routeId: string; name: string; data: FieldMappingsConfig }>>({});
    const [routeConfigData, setRouteConfigData] = useState<FieldMappingsConfig>({ field_mappings: {} });
    // The route_configuration's NAME, cached separately from field_mappings: a
    // PATCH replaces the whole configuration payload, so every save has to resend
    // it. Its id is not tracked — the backend resolves the configuration from the
    // route, and a route id is what the DELETE addresses.
    const [routeConfigurationName, setRouteConfigurationName] = useState<string>("");

    // Monotonic "please fetch the logs" counter, not a boolean flag. Several
    // triggers can fire in the same commit (e.g. the tab handler and the subpage
    // effect on a tab click); with a boolean, one of them writing `true` while it
    // was already `true` is a no-op — React skips the re-render, the effect never
    // re-runs, and the flag stays stuck `true` forever, so no further filter,
    // refresh, sort or page request is ever issued. Incrementing always registers.
    const [logsReloadToken, setLogsReloadToken] = useState(0);
    const requestLogsReload = useCallback(() => setLogsReloadToken(previous => previous + 1), []);
    const [loadingLogs, setLoadingLogs] = useState(false);
    // Unhealthy Route: whether the Warning-minimum default was already seeded
    // into logsFilter this page visit (see loadRouteDetails). A ref, not state:
    // it guards a one-shot and must survive the route reloads that re-run it.
    const warningDefaultSeeded = useRef(false);
    const [isAppendingLogs, setIsAppendingLogs] = useState(false);
    const [logsError, setLogsError] = useState(false);
    const currentRequestRef = useRef<AbortController | null>(null);
    const defaultTimezone = { label: "UTC", value: "+0000" };
    const [selectedTimezone, setSelectedTimezone] = useState<any>(() => {
        // `selectedTimezone` is stored as JSON. If we accidentally keep it as a raw string,
        // `selectedTimezone.value` becomes `undefined` and we end up appending "undefined" to dates.
        const stored = localStorage.getItem("selectedTimezone");
        if (!stored) return defaultTimezone;
        try {
            const parsed = JSON.parse(stored);
            return parsed && typeof parsed === "object" && typeof parsed.value === "string" ? parsed : defaultTimezone;
        } catch {
            return defaultTimezone;
        }
    });
    const [logs, setLogs] = useState<RouteLog[]>([]);
    const destinationOffset = 0;
    const [nextLogsCursor, setNextLogsCursor] = useState<string | null>(null);
    const [prevLogsCursor, setPrevLogsCursor] = useState<string | null>(null);
    const [selectedCursor, setSelectedCursor] = useState<string | null>(null);

    const debouncedSearch = useCallback(
        debounce((updatedFilter: LogFilterTypes) => {
            setIsAppendingLogs(false);
            setSelectedCursor(null);
            requestLogsReload();
        }, 300),
        []
    );

    const queueFilterSearch = useCallback((updatedFilter: LogFilterTypes) => {
        setLogsFilter(updatedFilter);
        setLoadingLogs(true);

        debouncedSearch(updatedFilter);
    }, [debouncedSearch]);

    useEffect(() => {
        setRouteHasDestinations(destinations.length > 0);
    }, [destinations]);

    // The Logs tab pins its filter under the nav by scrolling the page down. That
    // scroll offset is the window's, not the tab's, and a tab switch is a route
    // change — which never resets it — so every other tab used to open already
    // scrolled past its own header. Reset on each tab change. Keyed on the subpage
    // and not on the location: the Configurations panel navigates within
    // "configurations" (account/flow selection) and must keep the user's place.
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [effectiveSubpage]);

    useEffect(() => {
        const providerType = routeProvider.type;

        // The Route-rules guide is tied to the Rules panel, not the provider type, so
        // it's built independently of the provider-type guard below.
        const rulesCards: HelpCard[] = rulesPanelOpen ? [{
            title: t('Route Rules'),
            description: t('Learn how to transform and map data with Route rules.'),
            button: { label: t('Learn More'), url: 'https://support.earthranger.com/routes-gundi/route-rules' },
        }] : [];

        if (!providerType?.id || !providerType?.value) {
            setHelpContent(rulesCards.length > 0 ? { cards: rulesCards } : null);
            return;
        }

        const kind = getRouteKind(providerType);
        const cards: HelpCard[] = [
            // While the Rules side panel is open, surface the Route-rules guide first.
            ...rulesCards,
            // The flow map is the page's main content and a new idea, so it gets a
            // card of its own wherever it is on screen.
            ...(effectiveSubpage === "configurations" ? [{
                title: t('Configuring a Route'),
                description: t('See how data moves between the systems in this Route, and set up each direction.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/routes-gundi/routes' },
            }, {
                title: t('Add a Destination to a Route'),
                description: t('Learn the three ways to add a destination to a Route.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/routes-gundi/add-destination-to-route' },
            }] : []),
            ...(kind === 'webhook' ? [{
                title: t('Gundi Webhook API'),
                description: t('Learn how to send data to Gundi using webhooks.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/custom-webhook' },
            }] : []),
            ...((kind === 'push-only' || providerHasPushAction(providerType)) ? [{
                title: t('Gundi API'),
                description: t('Learn how to push data to Gundi using the API.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/gundi-api' },
            }] : []),
        ];
        setHelpContent(cards.length > 0 ? { cards } : null);
    }, [routeProvider.type, rulesPanelOpen, effectiveSubpage, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    // Coordinate the Help side panel with the Rules side panel across three width ranges:
    //   >= 1400px : both coexist side-by-side — shift Help + "?" left by the Rules width.
    //   768-1400  : not enough room for both, so close Help when Rules opens (see effect below).
    //   < 768px   : the md/hamburger breakpoint — Rules is full-screen and Help stacks
    //               full-screen on top of it, so there's nothing to shift (offset stays 0).
    // The offset is recomputed on resize so dragging across a breakpoint stays consistent.
    useEffect(() => {
        const RULES_PANEL_WIDTH = 520;
        const FULLSCREEN_MAX_WIDTH = 768;
        const applyOffset = () => {
            if (!rulesPanelOpen) {
                setHelpOffsetPx(0);
                return;
            }
            const width = typeof window !== "undefined" ? window.innerWidth : RULES_PANEL_WIDTH;
            setHelpOffsetPx(width < FULLSCREEN_MAX_WIDTH ? 0 : RULES_PANEL_WIDTH);
        };
        applyOffset();
        window.addEventListener("resize", applyOffset);
        return () => {
            window.removeEventListener("resize", applyOffset);
            setHelpOffsetPx(0);
        };
    }, [rulesPanelOpen, setHelpOffsetPx]);

    // One-shot when Rules opens: in the mid range Help can't sit beside it, so close Help and
    // let the user re-open it from the (shifted) "?". Below the full-screen breakpoint they
    // stack instead, so Help is left alone there.
    useEffect(() => {
        if (!rulesPanelOpen) return;
        const COEXIST_MIN_WIDTH = 1400;
        const FULLSCREEN_MAX_WIDTH = 768;
        const width = typeof window !== "undefined" ? window.innerWidth : COEXIST_MIN_WIDTH;
        if (width >= FULLSCREEN_MAX_WIDTH && width < COEXIST_MIN_WIDTH) {
            closeHelpPanel();
        }
    }, [rulesPanelOpen, closeHelpPanel]);

    useEffect(() => {
        return () => {
            if (debouncedSearch) {
                debouncedSearch.cancel();
            }
            if (currentRequestRef.current) {
                currentRequestRef.current.abort();
            }
        };
    }, [debouncedSearch]);

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);

    const [sources, setSources] = useState<Source[]>([]);
    const [totalSources, setTotalSources] = useState<number>(0);
    const [sourcesOffset, setSourcesOffset] = useState<number>(0);
    const [loadingSources, setLoadingSources] = useState(false);
    const [sourcesReloadToken, setSourcesReloadToken] = useState(0);
    const sourcesReloadPending = useRef(false);
    const requestSourcesReload = useCallback(() => {
        sourcesReloadPending.current = true;
        setLoadingSources(true);
        setSourcesReloadToken(previous => previous + 1);
    }, []);

    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);

    const [changesMade, setChangesMade] = useState(false);
    // Remove addDestModal state, not needed for page navigation
    const [updatingDestinations, setUpdatingDestinations] = useState(false);
    const [errors, setErrors] = useState({})
    const [testActionLoading, setTestActionLoading] = useState(false);
    const [testActionData, setTestActionData] = useState<any>(null);
    const navigate = useNavigate();

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let offset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadOrganizationsData = async (search_str: string = "") => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                if (search_str) {
                    const [orgs, , error] = await loadOrganizations(token, 0, search_str, []);
                    if (!error) setOrganizations(orgs);
                } else {
                    setOrganizations(await loadAllOrganizations(token));
                }
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let offset = 0;
                while (true) {
                    const [dest_types, total, error] = await loadDestinationTypes(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = dest_types;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    const loadDestinationsTab = async () => {
        await loadDestinations(destinations);
    };

    useEffect(() => {
        sessionStorage.setItem('routeSourceFilter', JSON.stringify(sourceFilter));
    }, [sourceFilter]);

    useEffect(() => {
        const loadPerms = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const temp = await getUserPermissions(token);
                    setCanAddDestination(temp);
                } catch (err) {
                    console.log(err)
                }
            }
        }

        // An invalid subpage renders the 404 page — skip the fetch cascade
        if (!pageLoaded && isValidSubpage) {
            loadPerms();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, pageLoaded, isValidSubpage]);

    // One effect per reload counter: each fires exactly once per request bump
    // (plus on token changes, so a request made before the token was ready — or
    // that failed — retries once a fresh token arrives, matching the old
    // boolean-flag behavior). The pending ref is cleared by the load on success.
    useEffect(() => {
        if (routeReloadPending.current && isValidSubpage) loadRouteDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, routeReloadToken, isValidSubpage]);

    useEffect(() => {
        if (routeOnlyReloadPending.current && pageLoaded) loadRouteDestinationsOnly();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, routeOnlyReloadToken, pageLoaded]);

    useEffect(() => {
        if (destinationsReloadPending.current && pageLoaded) loadDestinationsTab();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, destinationsReloadToken, pageLoaded]);

    // The add-destination drawer reuses the destinations tab's list machinery,
    // scoped to the Route's workspace; loadDestinations() already excludes
    // whatever is attached to this Route.
    useEffect(() => {
        if (!addDestinationDrawerOpen || !pageLoaded) return;
        setDrawerWorkspaceFilter(route.owner?.id ? { label: route.owner.name, value: route.owner.id } : null);
        setDestinationFilter((prev: FilterTypes) => ({
            ...prev,
            search: "",
            type__in: [],
            owner__in: route.owner?.id ? [{ name: route.owner.name, id: route.owner.id }] : [],
        }));
        setUpdatingDestinations(true);
        requestDestinationsReload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addDestinationDrawerOpen, pageLoaded]);

    // EarthRanger rows in the drawer show which ER account they use (username or
    // truncated token) — the list endpoint omits configurations, so enrich them.
    useEffect(() => {
        if (!addDestinationDrawerOpen || !destinationsNeedAuthEnrichment(existingDestPhase0)) return;
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        let cancelled = false;
        enrichDestinationsWithAuth(token, existingDestPhase0).then((enriched) => {
            if (!cancelled) setExistingDestPhase0(enriched);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addDestinationDrawerOpen, existingDestPhase0, token]);

    const handleDrawerWorkspaceChange = (option: { label: string; value: string } | null) => {
        setDrawerWorkspaceFilter(option);
        setDestinationFilter((prev: FilterTypes) => ({
            ...prev,
            owner__in: option ? [{ name: option.label, id: option.value }] : [],
        }));
        setUpdatingDestinations(true);
        requestDestinationsReload();
    };

    // Logs are driven by exactly one dependency — their own counter — so a fetch
    // happens once per requestLogsReload(), regardless of what else re-renders or
    // of the order effects happen to run in.
    useEffect(() => {
        if (logsReloadToken === 0) return;
        loadRouteLogs(destinations);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logsReloadToken]);

    useEffect(() => {
        if (sourcesReloadPending.current) loadRouteSources(routeProvider.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, sourcesReloadToken]);

    // Re-entering the logs tab (tab click, back navigation) or a destinations
    // change is always a fresh query, so drop any cursor left behind by infinite
    // scroll — reusing it would pin the reload to an old page of the result set
    // (same stale-page bug as refresh, reached through a different door). Routing
    // through the reload token (instead of calling loadRouteLogs directly)
    // lets the load pick up the cleared cursor on the next render.
    useEffect(() => {
        if (subpage === 'logs' && pageLoaded && !loadingLogs) {
            setSelectedCursor(null);
            setIsAppendingLogs(false);
            requestLogsReload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subpage, pageLoaded, destinations]);

    useEffect(() => {
        if (!pageLoaded) return;
        if (effectiveSubpage === 'sources' && !sourcesTabInitialized.current) {
            sourcesTabInitialized.current = true;
            requestSourcesReload();
        }
    }, [pageLoaded, effectiveSubpage]);

    useEffect(() => {
        if (!pageLoaded || filterDataInitialized.current) return;
        filterDataInitialized.current = true;
        Promise.all([
            loadDestinationURlsData(),
            loadDestinationTypesData(),
            loadOrganizationsData()
        ]);
    }, [pageLoaded]);

    useEffect(() => {
        if (location.state && location.state.fromAddRouteFlow) {
            setIsFromAddRouteFlow(true);
        }
    }, [location, subpage]);

    // LOAD FUNCTIONS
    // The URL carries the provider INTEGRATION id, not the route record id: the
    // integration id is what backend logs carry and what populates the source
    // provider id in EarthRanger, so a portal link can be correlated with a log
    // line on its own. Resolve the provider's connection first, then scope down
    // to its own route record — the page's `route` object keeps the connection
    // shape downstream code expects, but its name and destinations are THIS
    // route's, not the union across every route the provider feeds.
    const fetchRouteScoped = async (tkn: string) => {
        const conn = await getRouteDetails(tkn, route_id || "");
        if (!conn || !conn.owner?.id) return null;
        const recordId = routeRecordId(conn);
        // An integration with no route record has a Connection page but no Route
        // page, so the caller's 404 is the right answer. Returning the connection
        // unscoped is not an acceptable fallback: its `destinations` is the union
        // across ALL of the provider's routes, and the PATCHes on this page
        // target ONE record — presenting the union would let a save graft
        // another route's destinations on. Better to fail the load than to show
        // editable data that lies. A swallowed 401 also lands here, and there the
        // login modal is the right outcome.
        if (!recordId) return null;
        const own = await getRoute(tkn, recordId).catch((err: any) => {
            // 404/403 mean the record is gone or not visible to this user, which
            // is a real 404 for this page. Anything else — a 500, an offline
            // browser — is a load failure, and painting "this Route does not
            // exist" over a transient backend error tells the user something
            // false. Let it through; loadRouteDetails settles it without a 404.
            const status = err?.response?.status;
            if (status === 404 || status === 403) return null;
            throw err;
        });
        if (!own) return null;
        return { ...conn, destinations: own.destinations ?? [], default_route: { id: own.id, name: own.name } };
    };

    const loadRouteDestinationsOnly = async () => {
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) return;

        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && route_id) {
            try {
                const tempRoute = await fetchRouteScoped(token);
                if (!tempRoute) return;
                setDestinations(tempRoute.destinations);
                setRoute(tempRoute);
                setRouteName(routeDisplayName(tempRoute));
                setRouteHasDestinations(tempRoute.destinations.length > 0);
                routeOnlyReloadPending.current = false;
                requestDestinationsReload();
            } catch (err) {
                console.log('Failed to reload route:', err);
            }
        }
    }

    const loadRouteDetails = async () => {
        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && route_id) {
            try {
                const tempRoute = await fetchRouteScoped(token);
                if (!tempRoute) {
                    routeReloadPending.current = false;
                    // A swallowed 401 also resolves undefined. That is an expired
                    // session (the login modal is already up), not a missing
                    // Route — painting a 404 under it would strand the user on a
                    // false negative after logging back in.
                    if (!keycloak.isTokenExpired()) setNotFound(true);
                    return;
                }
                setDestinations(tempRoute.destinations);
                setRoute(tempRoute);
                setRouteName(routeDisplayName(tempRoute));
                setRouteHasDestinations(tempRoute.destinations.length > 0);
                // Unhealthy Route: seed the Warning-minimum default HERE, before
                // the first logs fetch, so a deep link to the Logs tab runs ONE
                // filtered query instead of an unfiltered fetch that the filter
                // panel's own default immediately aborts and redoes. Once per
                // page visit, and never over a level the user already picked;
                // LogsFilter's per-mount default stays the re-apply path after
                // a user clears it and returns to the tab.
                let logsFilterForLoad = logsFilter;
                if (tempRoute.status === "unhealthy" && !warningDefaultSeeded.current && logsFilter.log_level.length === 0) {
                    warningDefaultSeeded.current = true;
                    logsFilterForLoad = filterLogStatus({ value: "30" }, logsFilter, t);
                    setLogsFilter(logsFilterForLoad);
                }
                setDestinationFilter(filterDestinationOwner({
                    target: {
                        "name": tempRoute.owner.name,
                        "value": tempRoute.owner.id,
                    }
                }, destinationFilter));
                const savedtz = localStorage.getItem("selectedTimezone");
                setSelectedTimezone(savedtz ? JSON.parse(savedtz) : { label: "UTC", value: "+0000" });
                await Promise.all([
                    setCurrentMemberIsAdmin(await loadCurrentUserDetails(tempRoute.owner.id, token)),
                    loadRouteProvider(tempRoute.provider.id),
                    subpage === "logs" && loadRouteLogs(tempRoute.destinations, tempRoute.provider?.id, logsFilterForLoad),
                ]);
                routeReloadPending.current = false;
                // Reset only on success (not at the top of the try): an eager reset
                // flashes the loading state over the 404 page while a retry runs
                setNotFound(false);
                setPageLoaded(true);
            } catch (err: any) {
                // 403 is treated like 404: the entity is not viewable by this user
                if (err?.response?.status === 404 || err?.response?.status === 403) {
                    routeReloadPending.current = false;
                    setNotFound(true);
                    return;
                }
                // Anything else (500, offline) is not a 404, but the page must
                // still settle — clear the pending flag so a retry can re-fire.
                routeReloadPending.current = false;
                console.log('Failed to load route details:', err);
            }
        }
    }

    const buildDestinationsQuery = (offset: number) => ({
        base_url__in: destinationFilter.base_url__in.map(a => a.id).toString(),
        enabled: destinationFilter.enabled.map(a => a.id).toString(),
        type__in: destinationFilter.type__in.map(a => a.id).toString(),
        owner__in: destinationFilter.owner__in.map(a => a.id).toString(),
        ordering: destinationFilter.ordering,
        search: destinationFilter.search,
        offset,
        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset - page_size),
        next: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset + page_size),
    });

    // The drawer pages through the list the way the connections list does —
    // numbered pages that replace the rows — instead of an append-only "load
    // more" button.
    const handleDestinationsPageClick = async (e: any) => {
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        const offset = e.selected * page_size;
        // Which page click this is. Two clicks belong to the same query, so the
        // reload counter below cannot tell them apart — without a generation of
        // their own, clicking 2 then 3 would show page 2 if it resolved last.
        // The newest click wins, whatever order the responses arrive in.
        const requestId = destinationsPageRequestRef.current + 1;
        destinationsPageRequestRef.current = requestId;
        setUpdatingDestinations(true);
        // Which query this page belongs to. A filter or search change bumps the
        // reload counter, so a response that arrives after one is a page of a list
        // that no longer exists — showing it would mix in stale rows.
        const requestedFor = destinationsReloadTokenRef.current;
        try {
            const temp = await getDestinationsList(token, buildDestinationsQuery(offset));
            if (destinationsPageRequestRef.current !== requestId) return;
            if (!temp?.results || destinationsReloadTokenRef.current !== requestedFor) return;
            // Same exclusion the full load applies: what's already attached to
            // this Route can't be attached again.
            const excludedIds = destinations.map((d: Destination) => d.id);
            setExistingDestPhase0(temp.results.filter((d: Destination) => !excludedIds.includes(d.id)));
            setDestinationsTotalCount(temp.count ?? 0);
            setDestinationsPageOffset(offset);
        } catch (err) {
            console.log(err);
        } finally {
            // Only the newest click settles the loading state — an older response
            // clearing it would uncover the list while the current page is still
            // on its way.
            if (destinationsPageRequestRef.current === requestId) setUpdatingDestinations(false);
        }
    };

    // load all destinations, set availableDestinations as all destinations minus the ones already associated with this Route
    const loadDestinations = async (destArray: Destination[]) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setUpdatingDestinations(true);
            // Which query this load belongs to (same guard the page click has). Two
            // reloads in quick succession — remove a destination, then open the
            // drawer — could resolve out of order, and the older response replaced
            // the list under the user's cursor.
            const requestedFor = destinationsReloadTokenRef.current;
            try {
                const temp = await getDestinationsList(token, buildDestinationsQuery(destinationOffset));
                if (destinationsReloadTokenRef.current !== requestedFor) return;

                // check to see if destination in temp.result is in the selected array
                const tempDestArray: Destination[] = temp.results.filter((b: Destination) => !destArray.map((a: Destination) => a.id).includes(b.id))

                // A fresh query always lands on page one; the drawer's paginator
                // reads these to draw the same page controls the connections list has.
                setDestinationsTotalCount(temp.count ?? 0);
                setDestinationsPageOffset(destinationOffset);
                setExistingDestPhase0(tempDestArray);
                destinationsReloadPending.current = false;
            } catch (err) {
                console.log(err)
            } finally {
                // Settle the loading state (a failed request must not leave the UI
                // stuck) — but only for the newest query: an older request clearing
                // it would uncover the list while the current one is still loading.
                if (destinationsReloadTokenRef.current === requestedFor) setUpdatingDestinations(false);
            }
        }
    }

    const fetchRouteLogsWithBackoff = async (
        authToken: string,
        logQuery: any,
        abortSignal: AbortSignal,
        maxRetries = 2,
        requestTimeout = 30000 // 30 second timeout per request
    ) => {
        let attempt = 0;
        let delay = 500;

        // eslint-disable-next-line no-constant-condition
        while (true) {
            try {
                if (abortSignal.aborted) {
                    const abortError: any = new Error("Request aborted");
                    abortError.name = "AbortError";
                    throw abortError;
                }

                const result = await getRouteLog(authToken, logQuery, abortSignal, requestTimeout);
                return result;
            } catch (err: any) {
                // Handle abort/cancellation errors (don't retry these)
                if (err.name === 'AbortError' ||
                    err.name === 'CanceledError' ||
                    err.code === 'ERR_CANCELED' ||
                    err.code === 'ECONNABORTED' ||
                    abortSignal.aborted) {
                    throw err;
                }

                attempt += 1;
                if (attempt > maxRetries) {
                    throw err;
                }

                await new Promise(resolve => setTimeout(resolve, delay));
                delay *= 2;
                delay = Math.min(delay, 10000); // cap at 10 seconds
            }
        }
    };

    // `filterOverride` exists for the initial load only: when loadRouteDetails
    // seeds the unhealthy Warning default, the setLogsFilter it issues hasn't
    // re-rendered yet, so the state this closure sees is still the unseeded
    // filter — the caller hands the seeded one in directly.
    const loadRouteLogs = async (destination_ids: Destination[], provider_id?: string, filterOverride?: typeof logsFilter) => {
        const activeLogsFilter = filterOverride ?? logsFilter;
        console.log("Loading logs");
        setLogsError(false); // Clear any previous error

        if (currentRequestRef.current) {
            console.log("Cancelling previous request");
            currentRequestRef.current.abort();
        }

        const abortController = new AbortController();
        currentRequestRef.current = abortController;

        setLoadingLogs(true);

        let id_array: string[] = destination_ids.map(a => a.id);
        // integration__in takes INTEGRATION ids. The URL's route_id is a route
        // RECORD id since the decoupling, so the provider's own id has to come
        // from the route — pushing route_id dropped every provider-side log entry
        // (the bulk of a pull Route's logs). Callers that run inside the initial
        // load pass it explicitly: `route` state is still empty at that point.
        const providerIntegrationId = provider_id || route.provider?.id;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && providerIntegrationId) {
            try {
                id_array.push(providerIntegrationId);
                const log_query = {
                    cursor: selectedCursor,
                    integration__in: id_array.toString(),
                    ordering: activeLogsFilter.ordering,
                    log_level: activeLogsFilter.log_level.map(a => a.id).toString(),
                    log_type: activeLogsFilter.log_type.map(a => a.id).toString(),
                    from_date: activeLogsFilter.from_date.length > 0 ? activeLogsFilter.from_date[0].id : "",
                    to_date: activeLogsFilter.to_date.length > 0 ? activeLogsFilter.to_date[0].id : "",
                    search: activeLogsFilter.search
                };

                console.log("Fetching logs with query:", log_query);
                const temp = await fetchRouteLogsWithBackoff(token, log_query, abortController.signal);
                console.log("Log response received:", temp);

                if (currentRequestRef.current === abortController && !abortController.signal.aborted && temp) {
                    if (selectedCursor === nextLogsCursor && nextLogsCursor !== null) {
                        setLogs(prevLogs => [...prevLogs, ...temp.results]);
                    } else {
                        setLogs(temp.results || []);
                    }

                    const next_cursor = parseCursor(temp.next);
                    const prev_cursor = parseCursor(temp.previous);
                    setNextLogsCursor(next_cursor);
                    setPrevLogsCursor(prev_cursor);
                    setLogsError(false); // Clear error on success
                }
            } catch (err: any) {
                if (err.name === 'AbortError') {
                    console.log("Log request was cancelled");
                    // Don't set error state for aborted requests
                } else {
                    console.error("Error loading logs:", err);
                    if (currentRequestRef.current === abortController) {
                        setLogsError(true); // Set error state for actual failures
                    }
                }
            } finally {
                if (currentRequestRef.current === abortController) {
                    setLoadingLogs(false);
                    setIsAppendingLogs(false);
                }
            }
        } else {
            // No usable token/Route: reset both progress flags, not just the
            // spinner. A stuck isAppendingLogs would keep the "Loading..." row and
            // make the scroll handler bail, disabling infinite scroll.
            setLoadingLogs(false);
            setIsAppendingLogs(false);
        }
    }

    const loadRouteSources = async (provider_id: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const sourceQuery = {
                    search: sourceFilter.search,
                    destination_url__in: sourceFilter.destination_url__in.map(a => a.id).toString(),
                    ordering: sourceFilter.ordering,
                    offset: sourcesOffset,
                    previous: process.env.REACT_APP_BASE_URL_LOCAL + "/sources/?pager=limit&limit=" + page_size + "&offset=" + (sourcesOffset - page_size),
                    next: process.env.REACT_APP_BASE_URL_LOCAL + "/sources/?pager=limit&limit=" + page_size + "&offset=" + (sourcesOffset + page_size)
                }
                const temp = await getSourcesForRoute(token, provider_id, sourceQuery);
                setSources(temp.results);
                setTotalSources(temp.count);
                sourcesReloadPending.current = false;
            } catch (err) {
                console.log(err);
            } finally {
                // Always settle the spinner — a failed request must not leave the
                // refresh button disabled and spinning forever.
                setLoadingSources(false);
            }
        } else {
            // No usable token: settle the spinner; the request stays pending and
            // re-fires once a fresh token arrives.
            setLoadingSources(false);
        }
    }

    const loadRouteProvider = async (provider_id?: string) => {
        const integrationId = provider_id || route.provider?.id;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && integrationId) {
            const tempIntegration = await getIntegrationDetails(token, integrationId);
            setRouteProvider(tempIntegration)
        }
    }

    const loadRouteConfiguration = async () => {
        // Local name: this is the route RECORD id the PATCHes target. It is never
        // the page's :route_id URL param, which is the provider integration id.
        const recordId = defaultRouteId;
        if (!recordId) return;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const route = await getRouteConfiguration(token, recordId);
                const data: FieldMappingsConfig = route?.configuration?.data ?? { field_mappings: {} };
                setRouteConfigData(data);
                setRouteConfigurationName(route?.configuration?.name ?? "");
            } catch (err) {
                console.log('Failed to load route configuration:', err);
            }
        }
    }

    useEffect(() => {
        if (pageLoaded && defaultRouteId) {
            loadRouteConfiguration();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageLoaded, defaultRouteId, token]);

    // The provider and webhook tabs became sections of the flow map's provider
    // panel. Their URLs still exist in bookmarks, so send them to the equivalent
    // selection instead of rendering a tab that no longer exists.
    useEffect(() => {
        if (!route_id || !routeProvider?.id) return;
        if (effectiveSubpage !== "provider" && effectiveSubpage !== "webhook") return;
        navigate(`/routes/${route_id}`, {
            replace: true,
            state: {
                flowSelection: {
                    kind: "account",
                    integrationId: routeProvider.id,
                    section: effectiveSubpage === "webhook" ? "webhook" : undefined,
                },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route_id, routeProvider?.id, effectiveSubpage]);

    // Same URLs, but the provider never arrived: land on the flow map rather than
    // rendering a tab that no longer exists.
    useEffect(() => {
        if (!pageLoaded || !route_id || routeProvider?.id) return;
        if (effectiveSubpage !== "provider" && effectiveSubpage !== "webhook") return;
        navigate(`/routes/${route_id}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route_id, routeProvider?.id, effectiveSubpage]);

    // Arriving at the bare /routes/:id of an unhealthy Route opens Logs, because
    // that is where the reason lives. ONCE, on arrival — a standing rule would
    // make Configurations unreachable, since its tab sends the user back to this
    // same bare URL. After this fires the user drives, and the bare URL means
    // Configurations like it does everywhere else. The ref is per mount, and
    // RouteDetailsRoute keys the screen on :route_id, so a different Route gets
    // a fresh one.
    const unhealthyLandingSettled = useRef(false);
    useEffect(() => {
        if (unhealthyLandingSettled.current) return;
        // Wait for the real status: the placeholder Route reads healthy, and
        // latching on it would skip the redirect for every unhealthy Route.
        if (!pageLoaded || !route_id) return;
        unhealthyLandingSettled.current = true;
        // A caller that asked for a panel (or for the add-destination highlight)
        // has already chosen Configurations as surely as a named tab would have.
        // Landing on Logs would drop that request along with the state carrying
        // it — and an unhealthy Route is exactly the one a user is most likely
        // to be attaching a destination to.
        const askedForConfigurations = !!(location.state as any)?.flowSelection
            || !!(location.state as any)?.highlightAddDestination;
        if (subpage || askedForConfigurations || route.status !== "unhealthy") return;
        navigate(`/routes/${route_id}/logs`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageLoaded, route.status, subpage, route_id]);

    // The destinations and General (overview) tabs are gone too — the flow map
    // page carries both. Their URLs stay valid for bookmarks and land there.
    useEffect(() => {
        if (!route_id || (effectiveSubpage !== "destinations" && effectiveSubpage !== "overview")) return;
        navigate(`/routes/${route_id}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route_id, effectiveSubpage]);

    // SELECT SUB PAGE ON ROUTE DETAILS PAGE
    function handleSelectSubPage(e: string) {
        if (e === "logs") {
            setIsAppendingLogs(false);
            // Fresh entry to the tab must start from the first page — see the
            // subpage effect above for the matching reset on route-driven entry.
            setSelectedCursor(null);
            requestLogsReload();
        }
        // Configurations is what the bare URL shows, so it does not get a segment
        // of its own — otherwise the same tab would have two addresses.
        navigate(routeTabPath(route_id, e));
    }

    // HANDLE SAVE CHANGE METHODS
    // The Route's name is its own field on the route record — editing it must
    // not touch the provider Connection's name.
    function handleChangeRouteName(e: any) {
        setRouteName(e.target.value);
        setChangesMade(true);
    }

    function handleChangeRouteOwner(e: any) {
        setRouteProvider((prevProvider: Provider) => ({
            ...prevProvider,
            owner: { id: e.value, name: e.label, description: "" }
        }));
        setChangesMade(true);
    }

    function handleSearchOrgDropdown(e: any) {
        if (e.length > 1) loadOrganizationsData(e);
    }

    function handleLoadMoreOrgs() {
        loadOrganizationsData();
    }

    async function handleToggleProviderEnabled(e: any) {
        const updatedRoute = {
            "enabled": e,
        }
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                // A swallowed 401 comes back as undefined — not a save.
                const saved = await updateConnection(token, routeProvider.id, updatedRoute);
                if (!saved) return;
                toast.success(`${t("Route updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                setRouteProvider((prevProvider: Provider) => ({
                    ...prevProvider,
                    enabled: e,
                }))
                if (e) {
                    trackRouteEnabled(routeProvider.id, routeProvider.type?.name, route.owner?.name);
                } else {
                    trackRouteDisabled(routeProvider.id, routeProvider.type?.name, route.owner?.name);
                }
                requestRouteReload();
                setChangesMade(false);
            } catch (error) {
                console.log(error)
            }

        }
    }

    // `target` is the account the action belongs to — the flow map tests destination
    // accounts too, and an action must run against its own integration, not the
    // provider's. It defaults to the provider for screens that only show one account.
    const handleTestAction = async (action_id: string, action_type: string,
        target?: { id: string, configurations: any[] }, formData?: Record<string, unknown>) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setTestActionLoading(true);
            const account = target ?? { id: route.provider.id, configurations: routeProvider.configurations };
            const stored = (account.configurations || []).find((a: ConnectionConfiguration) => a.action.id === action_id);
            const form_data = formData ?? stored?.data ?? {};

            try {
                let result = await testConnectionAction(token, account.id, action_type, { config_overrides: Object(form_data) });
                if (result) {
                    let { data: resp_data } = result;
                    setTestActionData(resp_data);
                }
            } catch (err: any) {
                setTestActionData({ error: err?.response?.data?.detail || err?.message || "Server error. Please try again." });
            } finally {
                setTestActionLoading(false);
            }
        }
    }

    const formIsValid = () => {
        let form_errors: any = {};
        // validate the basic form
        if (routeName.trim().length === 0) form_errors.provider_name = t("Name is required.")
        if (!(routeProvider.owner?.name ?? route.owner.name)) form_errors.provider_owner = t("Select a workspace.")

        setErrors(form_errors);
        return Object.keys(form_errors).length === 0;
    }

    async function handleUpdateRoute() {
        // The name goes to the route record; everything else still lives on the
        // provider integration. Renaming the Route never renames the Connection.
        const updatedProvider = {
            "owner": routeProvider.owner?.id ?? route.owner.id,
            "type": routeProvider.type.id,
            "base_url": routeProvider.base_url,
            "enabled": routeProvider.enabled,
        }
        // Keep changesMade set: the draft is still unsaved, and the Save/Cancel
        // row is what lets the user fix or abandon it.
        if (!formIsValid()) return;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                if (defaultRouteId && routeName.trim() !== routeDisplayName(route)) {
                    await renameRoute(token, defaultRouteId, routeName.trim());
                }
                // Same as above: nothing back means nothing saved.
                const saved = await updateConnection(token, routeProvider.id, updatedProvider);
                if (!saved) return;
                toast.success(`${t("Route updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                requestRouteReload();
                // No navigation: the name lives in a bar above the flow map, and
                // navigating to the bare subpage dropped the configurations/*
                // selection, closing whichever panel the user had open.
                setChangesMade(false);
            } catch (error: any) {
                console.log(error)
                // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
                if (error?.response?.status === 401) return;
                toast.error(`${t("Something went wrong. Please try again or contact support.")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
            }

        }
    }

    async function handleUpdateWebhookConfiguration(data: Record<string, any>) {
        // `webhook` comes back as the record on some responses and as a bare id
        // on others.
        const webhookRef = routeProvider.webhook_configuration?.webhook;
        const webhookId = (typeof webhookRef === "string" ? webhookRef : webhookRef?.id)
            || routeProvider.type?.webhook?.id;
        // Whatever stops the save has to say so, or Save looks dead.
        if (!webhookId) {
            toast.error(`${t("Failed to save webhook configuration")}`, {
                position: toast.POSITION.TOP_RIGHT
            });
            return;
        }
        // No message for an expired session: the axios interceptor raises the
        // login modal, and calling it a webhook failure sends the user hunting
        // through their configuration.
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                // A swallowed 401 comes back as undefined — not a save, so the
                // draft stays put. Same check as useConfigEditor's.
                const saved = await updateConnection(token, routeProvider.id, {
                    webhook_configuration: {
                        webhook: webhookId,
                        data,
                    }
                });
                if (!saved) return;
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                requestRouteReload();
                setChangesMade(false);
            } catch (error) {
                console.log(error);
                toast.error(`${t("Failed to save webhook configuration")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
            }
        }
    }

    function handleCancelRouteChanges() {
        if (changesMade) {
            setShowConfirmationModal(true);
        } else {
            navigate("/");
            setChangesMade(false);
        }
    }

    const handleConfirmNavigation = () => {
        setShowConfirmationModal(false);
        setChangesMade(false);
        navigate("/");
    };

    const handleCancelNavigation = () => {
        setShowConfirmationModal(false);
    };

    // DESTINATION FILTERING FUNCTIONS
    function handleChangeGlobalDestinationSearch(searchSubStr: string) {
        setDestinationFilter(changeDestinationGlobalSearch(searchSubStr, destinationFilter));
        requestDestinationsReload();
    }

    const handleSelectDestinationTypeFilter = (selected: any) => {
        const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, type__in: tags }));
        requestDestinationsReload();
    }

    const handleSelectDestinationOwnerFilter = (selected: any) => {
        const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, owner__in: tags }));
        requestDestinationsReload();
    }

    const toSingleDestValue = (tags: { name: string, id: string }[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    // ADD AND REMOVE DESTINATIONS
    // Creating a destination for this Route. If exactly one technology filter is
    // active, preselect it so the user lands straight in configuration.
    const handleCreateDestination = () => {
        const preselect = destinationFilter.type__in.length === 1 ? destinationFilter.type__in[0].id : undefined;
        navigate(`/routes/${route_id}/${effectiveSubpage}/newdestination`,
            preselect ? { state: { preselectTypeId: preselect } } : undefined);
    }

    const handleAddNewDestination = async (dest: Destination) => {
        if (!defaultRouteId) {
            console.log("cannot add destination: route has no route", route.id);
            toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        // Remove dest from available destinations
        const availableArray: Destination[] = existingDestPhase0.filter((availableDest: Destination) => availableDest.id !== dest.id);
        setExistingDestPhase0(availableArray);

        // Add dest to Route (state)
        const updatedDestinations: Destination[] = [...destinations, dest];
        setDestinations(updatedDestinations);

        // Prepare unique destination IDs
        const destinationIds = Array.from(new Set(updatedDestinations.map(a => a.id)));
        const destination_ids = {
            destinations: destinationIds
        };
        setChangesMade(true);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await addRouteDestinations(token, defaultRouteId, destination_ids);
                trackDestinationAdded(route.id, dest.type?.name, route.owner?.name);
                toast.success(`${t("Route updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                // Reload Route details to ensure state is in sync
                await loadRouteDetails();
                navigate(`/routes/${route_id}`, { replace: true, state: { flowSelection: { kind: "account", integrationId: dest.id } } });
                setChangesMade(false);
            } catch (error) {
                console.log(error)
                toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
            }
        }
    }

    // Drawer path: attach the checked destinations, then select the first one on
    // the flow map so its node is the panel's subject.
    const handleAttachDestinationsFromDrawer = async (ids: string[]) => {
        if (ids.length === 0) return;
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        const added = existingDestPhase0.filter((d: Destination) => ids.includes(d.id));
        // Local name: this is the route RECORD id the PATCHes target. It is never
        // the page's :route_id URL param, which is the provider integration id.
        const recordId = defaultRouteId;
        if (!recordId) {
            console.log("cannot attach destinations: route has no default route", route.id);
            toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const destination_ids = { destinations: Array.from(new Set([...destinations.map((d) => d.id), ...ids])) };
        try {
            const updated = await addRouteDestinations(token, recordId, destination_ids);
            // A swallowed 401 resolves undefined — not an attach, so don't report one.
            if (!updated) return;
            added.forEach((d: Destination) => trackDestinationAdded(route.id, d.type?.name, route.owner?.name));
            toast.success(`${t("Route updated")}`, { position: toast.POSITION.TOP_RIGHT });
            // The attached destinations are no longer available — leaving them in
            // place let a later remove push a duplicate copy.
            setExistingDestPhase0((prev: Destination[]) => prev.filter((d: Destination) => !ids.includes(d.id)));
            setAddDestinationDrawerOpen(false);
            requestRouteReload();
            // The URL keeps the id the page is addressed by (same rule as the
            // remove path), so attaching never relocates the user mid-flow.
            navigate(`/routes/${route_id}`, { replace: true, state: { flowSelection: { kind: "account", integrationId: ids[0] } } });
        } catch (error) {
            console.log(error);
            toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
        }
    }

    const handleRemoveDestination = async (dest: Destination) => {
        // The removed destination is NOT put back into the available list here.
        // Every surface that shows that list refetches it on open, so the server
        // is the source of truth — the optimistic push only ever produced stale
        // rows (and, before the dedupe, duplicate React keys) that flashed in the
        // drawer ahead of the real list and stole clicks meant for other rows.

        // remove dest from Route
        let tempArray: Destination[] = [];
        destinations.forEach((currentDest: Destination) => {
            if (dest.id !== currentDest.id) tempArray.push(currentDest)
            setDestinations(tempArray);
        })
        const destination_ids = {
            "destinations": tempArray.map(a => a.id)
        }
        setChangesMade(true);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            // The destinations list lives on the Route's underlying route, so without one
            // there is nothing to PATCH — say so instead of failing silently.
            // Local name: this is the route RECORD id the PATCHes target. It is
            // never the page's :route_id URL param, the provider integration id.
            const recordId = defaultRouteId;
            if (!recordId) {
                console.log("cannot remove destination: route has no default route", route.id);
                toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
                setDestinations(destinations);
                setChangesMade(false);
                return;
            }
            // A destination that sends data back has its own reverse route, and the
            // arrows for it are drawn from route.destinations — detaching without
            // deleting it left that route running with nothing in this Route able to
            // stop it. Delete it first: if the PATCH below fails the user still sees
            // the destination, but nothing is silently pumping data.
            const reverseIds = reverseRoutesBySite[dest.id] || [];
            try {
                for (const reverseId of reverseIds) await deleteRoute(token, reverseId);
            } catch (err: any) {
                console.log("failed to delete reverse route while removing destination", err);
                if (err?.response?.status !== 401) {
                    toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
                }
                setDestinations(destinations);
                setChangesMade(false);
                return;
            }
            try {
                await addRouteDestinations(token, recordId, destination_ids);
                // Its field mapping is per flow, so it goes with the flow — otherwise
                // re-attaching the same destination silently revives the old rule.
                if (getRuleForDestination(routeConfigData, routeProvider?.id ?? "", dest.id)) {
                    try {
                        await updateRouteConfiguration(token, recordId, {
                            name: routeConfigurationName || route.provider?.name || "",
                            data: buildConfigWithoutRule(routeConfigData, routeProvider?.id ?? "", dest.id),
                        });
                    } catch (ruleErr) {
                        // The destination is detached either way; a stale mapping is
                        // not worth failing the removal over.
                        console.log("failed to drop the removed destination's rule", ruleErr);
                    }
                }
                toast.success(`${t("Route updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                if (reverseIds.length > 0) setReverseReloadToken((n) => n + 1);
                // The PATCH targets the route record; the URL keeps the provider
                // integration id the page is already addressed by. Routed through
                // routeTabPath so this cannot mint /routes/:id/configurations,
                // which the router answers with a redirect — the page would tear
                // down and rebuild, losing the selection the removal just set.
                navigate(routeTabPath(route_id, effectiveSubpage));
                setChangesMade(false);
            } catch (error) {
                console.log(error)
                toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
            }

        }
    }

    // TRANSFORMATION RULES
    const handleOpenRules = (destination: Destination, direction: FlowDirectionKey = "out") => {
        setRulesPanelDestination(destination);
        setRulesDirection(direction);
        setRulesPanelOpen(true);
    }

    // Everything the rules panel needs about the flow it was opened for. The two
    // directions are two different route records with the provider/destination
    // roles swapped, so resolving them in one place keeps save, delete and the
    // panel's own display from disagreeing about which flow is being edited.
    const rulesTarget = useMemo(() => {
        const resolved = resolveRulesTarget({
            direction: rulesDirection,
            site: rulesPanelDestination,
            provider: routeProvider ?? route.provider,
            routeRecordId: defaultRouteId,
            routeConfigName: routeConfigurationName,
            routeConfigData: routeConfigData,
            reverseConfigBySite,
        });
        if (!resolved) return null;
        // The panel labels the other end of the flow, so it needs the record, not
        // just its id: the site going out, our provider coming back.
        const counterpart = (rulesDirection === "out"
            ? rulesPanelDestination
            : (routeProvider ?? route.provider)) as Destination;
        return { ...resolved, counterpart };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rulesPanelDestination, rulesDirection, defaultRouteId, routeProvider, routeConfigurationName, routeConfigData, reverseConfigBySite, route.provider]);

    // Where a saved/deleted rule lands: this Route's own configuration state, or
    // the cached configuration of the reverse Route.
    const storeRulesConfig = (data: FieldMappingsConfig, name?: string) => {
        const site = rulesPanelDestination;
        if (rulesDirection === "out") {
            setRouteConfigData(data);
            if (name !== undefined) setRouteConfigurationName(name);
            return;
        }
        if (!site) return;
        setReverseConfigBySite((prev) => {
            const current = prev[site.id];
            if (!current) return prev;
            return { ...prev, [site.id]: { ...current, data, name: name ?? current.name } };
        });
    }

    const handleCloseRules = () => {
        setRulesPanelOpen(false);
    }

    const handleSaveRules = async (
        streamType: StreamType,
        mapping: FieldMapping,
        name: string
    ): Promise<FieldErrors | void> => {
        // The record the PATCH targets is the flow's OWN route: this Route for the
        // outgoing direction, the reverse Route for the incoming one. Neither is
        // the page's :route_id URL param, which is the provider integration id.
        const recordId = rulesTarget?.recordId;
        const providerId = rulesTarget?.providerId;
        const destinationId = rulesTarget?.counterpart?.id;
        if (!recordId || !providerId || !destinationId) {
            // Field mappings live on the route's configuration, so with no route
            // there is nothing to save. Returning quietly reads as a dead button.
            console.log("cannot save rule", { recordId, providerId, destinationId });
            toast.error(`${t("Failed to save rule")}`, { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        // buildConfigWithRule preserves any other keys (subject_type, ca_uuids, …) that
        // the route's configuration.data already carries, so the PATCH-replaces-data
        // semantics don't blow them away.
        const nextConfig = buildConfigWithRule(rulesTarget.data, providerId, destinationId, streamType, mapping);
        try {
            await updateRouteConfiguration(token, recordId, {
                name,
                data: nextConfig,
            });
            storeRulesConfig(nextConfig, name);
            toast.success(`${t("Rule saved")}`, { position: toast.POSITION.TOP_RIGHT });
        } catch (err: any) {
            // 400s carry field-level validation errors keyed by dotted path. We surface
            // them on the panel so the user knows what to fix; anything else falls back
            // to the generic save-failed toast.
            const responseStatus = err?.response?.status;
            if (responseStatus === 400 && err.response?.data) {
                const fieldErrors = parseServerErrors(err.response.data, providerId, destinationId, streamType);
                if (Object.keys(fieldErrors).length > 0) return fieldErrors;
            }
            console.log(err);
            toast.error(`${t("Failed to save rule")}`, { position: toast.POSITION.TOP_RIGHT });
        }
    }

    const handleDeleteRule = async () => {
        // Same resolution as the save above: the flow's own route record, with the
        // provider/destination roles as that direction sees them.
        const recordId = rulesTarget?.recordId;
        const providerId = rulesTarget?.providerId;
        const destinationId = rulesTarget?.counterpart?.id;
        if (!recordId || !providerId || !destinationId) {
            // Field mappings live on the route's configuration, so with no route
            // there is nothing to delete. Returning quietly reads as a dead button.
            console.log("cannot delete rule", { recordId, providerId, destinationId });
            toast.error(`${t("Failed to delete rule")}`, { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        try {
            const nextConfig = buildConfigWithoutRule(rulesTarget.data, providerId, destinationId);
            const fieldMappingsEmpty =
                !nextConfig.field_mappings || Object.keys(nextConfig.field_mappings).length === 0;
            const otherKeysEmpty = Object.keys(nextConfig).filter((k) => k !== "field_mappings").length === 0;
            const isEffectivelyEmpty = fieldMappingsEmpty && otherKeysEmpty;

            if (isEffectivelyEmpty) {
                // Try the dedicated DELETE endpoint first so the backend doesn't keep an
                // empty row around. If it isn't deployed yet on the env we're hitting,
                // some infra returns the SPA's index.html for unknown API paths and our
                // apiUtils interceptor reads that as a Keycloak redirect — falsely
                // triggering the session-expired modal. To keep delete working until the
                // backend PR is merged we fall back to a PATCH with empty data on any
                // error other than a real auth failure.
                try {
                    await deleteRouteConfiguration(token, recordId);
                    storeRulesConfig({ field_mappings: {} }, "");
                } catch (deleteErr: any) {
                    if (deleteErr?.response?.status === 401) throw deleteErr;
                    console.log("DELETE configuration not available, falling back to PATCH:", deleteErr);
                    await updateRouteConfiguration(token, recordId, {
                        name: rulesTarget.configName,
                        data: nextConfig,
                    });
                    storeRulesConfig(nextConfig);
                }
            } else {
                // Other rules / sibling keys remain — PATCH with the cleaned data.
                await updateRouteConfiguration(token, recordId, {
                    name: rulesTarget.configName,
                    data: nextConfig,
                });
                storeRulesConfig(nextConfig);
            }
            toast.success(`${t("Rule deleted")}`, { position: toast.POSITION.TOP_RIGHT });
        } catch (err) {
            console.log(err);
            toast.error(`${t("Failed to delete rule")}`, { position: toast.POSITION.TOP_RIGHT });
        }
    }

    const currentPanelRule = useMemo(() => {
        if (!rulesTarget?.providerId || !rulesTarget.counterpart?.id) return null;
        return getRuleForDestination(rulesTarget.data, rulesTarget.providerId, rulesTarget.counterpart.id);
    }, [rulesTarget]);

    // IDs of destinations that currently have a transformation rule under this
    // provider. Drives the rule badge on the flow map's outgoing arrows.
    const destinationsWithRules = useMemo<Set<string>>(() => {
        const ids = new Set<string>();
        const providerId = routeProvider?.id;
        const providerEntry = providerId ? routeConfigData.field_mappings?.[providerId] : undefined;
        if (!providerEntry) return ids;
        for (const streamType of STREAM_TYPES) {
            const streamEntry = providerEntry[streamType];
            if (streamEntry) Object.keys(streamEntry).forEach((id) => ids.add(id));
        }
        return ids;
    }, [routeConfigData, routeProvider?.id]);

    // Same badge for the way back: the reverse Route's own configuration, where
    // the site is the provider and our provider is the destination.
    const reverseDestinationsWithRules = useMemo<Set<string>>(() => {
        const ids = new Set<string>();
        const providerId = routeProvider?.id;
        if (!providerId) return ids;
        Object.entries(reverseConfigBySite).forEach(([siteId, reverse]) => {
            const siteEntry = reverse.data?.field_mappings?.[siteId];
            if (!siteEntry) return;
            for (const streamType of STREAM_TYPES) {
                if (siteEntry[streamType]?.[providerId]) ids.add(siteId);
            }
        });
        return ids;
    }, [reverseConfigBySite, routeProvider?.id]);

    // ── Bidirectional flows (GUNDI-5595) ────────────────────────────────────
    // The reverse direction is a second route: this Route's destination
    // becomes the data provider, and this Route's provider becomes its
    // destination. Both routes reuse the SAME integration records, and the UI
    // presents them as one bidirectional flow.
    // siteId -> route ids carrying data from that site back to our provider.
    const [reverseRoutesBySite, setReverseRoutesBySite] = useState<Record<string, string[]>>({});
    const [reverseReloadToken, setReverseReloadToken] = useState(0);
    // Other Routes that send data to our provider. Action configurations live
    // on the integration record, so edits here are inherited by those flows too.
    const [providerSharedWith, setProviderSharedWith] = useState<{ id: string, name: string }[]>([]);
    // The other Routes this same provider integration feeds. `enabled` is a field
    // on that integration, so disabling from one Route's map stops all of them —
    // this is what the confirmation has to name. Distinct from
    // providerSharedWith, which is the opposite direction (Routes sending INTO
    // our provider) and shares action configurations rather than enabled state.
    // The count is the API's total, not the fetched page's length: the request is
    // capped at page_size, and a provider feeding 40 Routes must not be described
    // as feeding 19. `null` means the lookup failed — "no other Routes" is unknown,
    // so the confirmation takes its cautious wording instead.
    const [providerAlsoProvidesFor, setProviderAlsoProvidesFor] = useState<number | null>(0);
    useEffect(() => {
        const providerId = routeProvider?.id;
        if (!providerId || !(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        let cancelled = false;
        getRoutesByIntegration(token, "provider", providerId).then((data) => {
            if (cancelled) return;
            if (!data.ok) return setProviderAlsoProvidesFor(null);
            // Every Route of this provider except the one on screen. The one on
            // screen is by definition one of them, so it always comes off the
            // total — checking whether it appears in the fetched page would
            // over-count by one for a provider with more Routes than fit a page.
            const total = data.count ?? data.results.length;
            setProviderAlsoProvidesFor(Math.max(0, total - (defaultRouteId ? 1 : 0)));
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, routeProvider?.id, defaultRouteId]);

    useEffect(() => {
        const providerId = routeProvider?.id;
        if (!providerId || !(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        let cancelled = false;
        (async () => {
            try {
                // Routes whose destination is OUR provider = candidate reverse flows.
                const data = await getReverseRoutes(token, providerId);
                if (cancelled) return;
                const siteIds = new Set(route.destinations.map((d) => d.id));
                const map: Record<string, string[]> = {};
                const shared: { id: string, name: string }[] = [];
                // ?destination_id= selects Routes, not routing rules: a sender that also
                // routes elsewhere returns ALL of its routing rules. Deleting those to
                // turn one direction off would destroy unrelated flows, so keep only the
                // rules that actually route to this Route's provider.
                const senders = (data?.results || data || []).filter((c: any) => c?.provider?.id);
                const routesToProvider = async (rules: any[]) => {
                    const kept: string[] = [];
                    for (const rule of rules) {
                        if (!rule?.id) continue;
                        const declared = rule.destinations;
                        if (Array.isArray(declared)) {
                            if (declared.some((d: any) => (d?.id || d) === providerId)) kept.push(rule.id);
                            continue;
                        }
                        // The rule came back without its destinations — ask the Route.
                        try {
                            const route = await getRouteConfiguration(token, rule.id);
                            const dests = route?.destinations || [];
                            if (dests.some((d: any) => (d?.id || d) === providerId)) kept.push(rule.id);
                        } catch (e) {
                            console.log("could not resolve route destinations", rule.id, e);
                        }
                    }
                    return kept;
                };
                for (const c of senders) {
                    if (siteIds.has(c.provider.id)) {
                        map[c.provider.id] = await routesToProvider(c.routing_rules || []);
                    } else {
                        // Anything else is an unrelated Route sharing this account.
                        // Link by the provider INTEGRATION id — that's what route
                        // URLs address. Still require a route record: a provider
                        // with none has no Route page to link to.
                        if (routeRecordId(c) && c.id) shared.push({ id: c.id, name: c.provider.name });
                    }
                }
                if (cancelled) return;
                setReverseRoutesBySite(map);
                setProviderSharedWith(shared);
                // The reverse flows' field mappings live on those route records, so
                // fetch each one's configuration — the "in" arrow has nothing to
                // show (or to edit) without it. First route per site: that is the
                // one the enable/disable toggle created and addresses.
                const configs: Record<string, { routeId: string; name: string; data: FieldMappingsConfig }> = {};
                // Concurrently: one Route can have many bidirectional sites, and
                // serial fetches add a round-trip of latency each before any
                // incoming arrow can show its rules.
                await Promise.all(Object.entries(map).map(async ([siteId, ids]) => {
                    const reverseId = ids[0];
                    if (!reverseId) return;
                    try {
                        const reverseRoute = await getRouteConfiguration(token, reverseId);
                        configs[siteId] = {
                            routeId: reverseId,
                            name: reverseRoute?.configuration?.name ?? "",
                            data: reverseRoute?.configuration?.data ?? { field_mappings: {} },
                        };
                    } catch (e) {
                        console.log("could not load reverse route configuration", reverseId, e);
                    }
                }));
                if (cancelled) return;
                setReverseConfigBySite(configs);
            } catch (e) {
                console.log("reverse route lookup failed", e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, routeProvider?.id, route.destinations, reverseReloadToken]);

    const handleEnableReverseFlow = async (dest: Destination) => {
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        const ownerId = route.owner?.id || routeProvider.owner?.id;
        if (!ownerId) return;
        try {
            await createRoute(token, {
                // The list relies on this exact shape to spot the reverse Route (hideReverseRoutes).
                name: reverseRouteName(dest.name, routeProvider.name),
                owner: ownerId,
                data_providers: [dest.id],
                destinations: [routeProvider.id],
            });
            toast.success(`${t("Route updated")}`, { position: toast.POSITION.TOP_RIGHT });
            setReverseReloadToken((n) => n + 1);
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status === 401) return;
            toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
        }
    };

    const handleDisableReverseFlow = async (dest: Destination) => {
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        const routeIds = reverseRoutesBySite[dest.id] || [];
        if (routeIds.length === 0) {
            // The reverse flow exists (that is why the toggle is on) but we could not
            // resolve which route carries it. Deleting a guess could take out unrelated
            // Routes, so say so instead.
            console.log("no resolved reverse route to delete for", dest.id);
            toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        try {
            for (const routeId of routeIds) await deleteRoute(token, routeId);
            toast.success(`${t("Route updated")}`, { position: toast.POSITION.TOP_RIGHT });
            setReverseReloadToken((n) => n + 1);
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status === 401) return;
            toast.error(`${t("Something went wrong. Please try again or contact support.")}`, { position: toast.POSITION.TOP_RIGHT });
        }
    };

    // SOURCE FILTER FUNCTIONS
    function handleChangeGlobalSourceSearch(searchSubStr: string) {
        setSourceFilter(changeSourceGlobalSearch(searchSubStr, sourceFilter));
        setSourcesOffset(0);
        requestSourcesReload();
    }

    const handleSelectSourceUrlFilter = (selected: any) => {
        const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter((prev: any) => ({ ...prev, destination_url__in: tags }));
        setSourcesOffset(0);
        requestSourcesReload();
    }

    const toSingleSourceValue = (tags: { name: string, id: string }[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const handleSortSources = (selected: { label: string, value: string }) => {
        setSourceFilter((prevFilter: any) => ({
            ...prevFilter,
            ordering: selected.value
        }));
        setSourcesOffset(0);
        requestSourcesReload();
    }

    const handleSourcePageClick = (e: any) => {
        setSourcesOffset(e.selected * page_size);
        requestSourcesReload();
    }

    // LOGS FILTERING AND SORTING FUNCTIONS
    const handleRefreshLogs = () => {
        console.log("Refreshing logs");

        // If a relative duration is active, recalculate from_date from "now"
        try {
            const raw = sessionStorage.getItem("routeLogsDateFilter");
            if (raw) {
                const saved = JSON.parse(raw);
                const durationInput = saved.type === "relative" ? saved.input
                    : saved.type === "preset" && saved.value !== "custom" ? saved.value
                        : null;
                if (durationInput) {
                    const freshDate = parseDuration(durationInput);
                    if (freshDate) {
                        setLogsFilter(prev => ({
                            ...prev,
                            from_date: [{
                                name: t("Start Date & Time"),
                                id: freshDate.toISOString(),
                                attribute_name: "datetime"
                            }]
                        }));
                    }
                }
            }
        } catch { /* ignore parse errors */ }

        setIsAppendingLogs(false);
        // Start over from the first page: a cursor left behind by infinite scroll
        // pins the query to an old position in the result set, so keeping it would
        // replay the same stale page forever instead of fetching the newest logs.
        setSelectedCursor(null);
        requestLogsReload();
    }

    const handleNextLogPageClick = () => {
        if (nextLogsCursor) {
            setSelectedCursor(nextLogsCursor);  // Set the cursor to nextCursor
            setIsAppendingLogs(true);
            requestLogsReload();
        }
    }

    const handlePrevLogPageClick = () => {
        if (prevLogsCursor) {
            setIsAppendingLogs(false);
            setSelectedCursor(prevLogsCursor);  // Set the cursor to prevCursor
            requestLogsReload();
        }
    }

    const handleSortLogTimestamp = (direction: boolean) => {
        console.log("Sorting logs by timestamp in direction: ", direction);
        setIsAppendingLogs(false);
        setLogsFilter((prevFilter: LogFilterTypes) => ({
            ...prevFilter,
            ordering: direction ? "created_at" : "-created_at"
        }));
        setSelectedCursor(null);
        requestLogsReload();
    }

    const handleGlobalLogSearch = (searchSubStr: string) => {
        const updatedFilter = logGlobalSearch(searchSubStr, logsFilter);
        queueFilterSearch(updatedFilter);
    };

    const handleSelectTimezone = (e: any) => {
        setSelectedTimezone(e);
        try {
            localStorage.setItem("selectedTimezone", JSON.stringify(e));
        } catch {
            // ignore storage failures (private mode / quota / etc.)
        }
    }

    const handleFilterLogType = (e: any) => {
        console.log("Filtering logs by type: ", e);
        const updatedFilter = filterLogType(e, logsFilter, t);
        queueFilterSearch(updatedFilter);
    };

    const handleFilterLogStatus = (e: any) => {
        console.log("Filtering logs by status: ", e);
        const updatedFilter = filterLogStatus(e, logsFilter, t);
        queueFilterSearch(updatedFilter);
    };

    const handleFilterLogsByDate = (e: any, side: string) => {
        console.log("Filtering logs by date: ", e, " side: ", side);
        setLogsFilter(prevFilter => filterLogDate(e, side, prevFilter, t));
        setLoadingLogs(true);
        debouncedSearch(logsFilter);
    };

    function handleResetLogFilters() {
        console.log("Resetting log filters");
        const updatedFilter = resetLogFilters();
        queueFilterSearch(updatedFilter);
    }

    async function handleSelectLog(log_id: string) {
        if (log_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            const temp = await getLogDetails(token, log_id);
            return temp;
        }
    }

    const skeletonBar = (w: string, h: string = "h-4") => (
        <div className={`${h} ${w} ${skeletonClasses.base}`} />
    );

    // A type that carries a webhook is a webhook here even when it also has pull
    // config (kind would say "standard") — the same rule computeNeedsSetup and the
    // node panel apply, or the banner would count a missing payload parsing it
    // never shows a step for.
    const isWebhookRoute = routeProvider.type != null
        && (getRouteKind(routeProvider.type) === 'webhook' || !!routeProvider.type.webhook);
    // The record's presence isn't enough — ConfigurationsTab synthesizes one with
    // null data, and computeNeedsSetup requires .data too. Same rule, same answer.
    const webhookConfigured = !!routeProvider.webhook_configuration?.data;
    // Each Quick Start step reports on its own thing: you can attach a destination
    // before finishing the provider's credentials, so the checks are independent
    // rather than a sequence where reaching step three implies step two is done.
    // Three states, not two: a step the user hasn't reached yet is "upcoming", one
    // that is finished is "completed", and one whose thing exists but isn't
    // configured yet is "attention" — attaching a destination doesn't finish the
    // step if that destination still has required fields empty.
    // Only the Configurations tab reports pendingSetup, so on every other tab it
    // stays null — "not reported yet" must not be read as "not configured", or the
    // banner would claim nothing is done at all while sitting on Logs or Sources.
    const setupLoaded = pendingSetup !== null;
    const providerConfigured = !!pendingSetup && !pendingSetup.provider;
    const quickStartProviderStatus: StepStatus =
        !setupLoaded ? "in-progress" : providerConfigured ? "completed" : "attention";
    const quickStartStepTwoStatus: StepStatus =
        !routeHasDestinations ? "upcoming"
            : !setupLoaded || !pendingSetup.destinations ? "completed" : "attention";
    const quickStartWebhookStepStatus: StepStatus = webhookConfigured ? "completed" : "attention";
    // The banner exists only to remind the user what's left, so it renders only
    // while something is known to be missing — no destination yet, a webhook
    // route without its payload parsing, or the flow map reporting empty required
    // fields. Purely derived: it comes back the moment configuration goes missing
    // again, and a fully configured Route simply has no banner (no success step,
    // no dismiss memory).
    // GUNDI-5680 — TEMPORARY HACK: no Quick Start while InReach is the data
    // provider. NOTE for reviewers: this is an accepted, intentional TEMPORARY hack (GUNDI-5680) — not an oversight to flag. It is removed by the same schema/API work that retires tempExemptions.ts.
    // Hiding the whole banner (not just the affected steps) is a deliberate
    // product call for the interim. In the InReach ↔ EarthRanger flow EarthRanger is a data provider
    // too, but we don't need the user to configure its "pull events" and "pull
    // observations" parameters even though the schema marks them required — the
    // periodic pull tasks aren't used in this integration. EarthRanger only
    // streams the data received by the sensors API, using the API key generated
    // for that connection. Until the schemas (or the API) can say so, the banner
    // would nag about steps that don't apply, so it is hidden for InReach Routes.
    // Replace with a more elegant solution and drop this condition.
    const quickStartHiddenForInReach = routeProvider.type?.value === INREACH_TYPE;
    const quickStartHasWork = !quickStartHiddenForInReach && (!routeHasDestinations
        || (isWebhookRoute && !webhookConfigured)
        || (!!pendingSetup && pendingSetup.total > 0));

    if (notFound || !isValidSubpage) return <NotFoundPage />;

    if (!pageLoaded) return (
        /* Same tint and bleed as the loaded page below, so the skeleton is not a
           white screen that turns teal the moment the data lands. */
        <div className="relative h-full bg-teal-t10 -mx-8 desktop:-mx-[60px] px-8 desktop:px-[60px] -mb-6 pb-6 md:min-h-[calc(100vh-70px)]"
            role="status" aria-busy="true">
            <span className="sr-only">{t("Loading…")}</span>
            {/* Header skeleton */}
            <div className={routeDetailsClasses.header}>
                <div className="max-w-list-table mx-auto">
                    <div className="flex flex-row justify-between mb-3">
                        <div className="flex flex-col gap-2 w-3/4">
                            {skeletonBar("w-64", "h-8")}
                            <div className="flex flex-row gap-2 mt-1">
                                {skeletonBar("w-24")}
                                {skeletonBar("w-32")}
                            </div>
                        </div>
                        {skeletonBar("w-40", "h-8")}
                    </div>
                    {/* Tabs skeleton — General, Provider, Destinations, Sources, Logs */}
                    <div className="flex flex-row gap-6 pt-1">
                        {["w-16", "w-16", "w-24", "w-16", "w-10"].map((w, i) => (
                            <div key={i} className={`${w} h-4 ${skeletonClasses.base} mb-3`} />
                        ))}
                    </div>
                </div>
            </div>
            {/* Content skeleton — varies by subpage */}
            {effectiveSubpage === "configurations" ? (
                <div className="mt-8 max-w-list-table mx-auto px-4">
                    {/* Flow map card */}
                    <div className="border border-teal-t30 rounded-lg bg-white mb-6">
                        <div className="flex items-center gap-3 px-5 py-3 border-b border-unlisted-gray-lines">
                            {skeletonBar("w-28", "h-5")}
                            {skeletonBar("w-40", "h-9 ml-auto")}
                        </div>
                        <div className="px-5 py-8 flex items-center gap-16">
                            {skeletonBar("w-[260px]", "h-[92px]")}
                            <div className="flex flex-col gap-4">
                                {skeletonBar("w-[260px]", "h-[92px]")}
                                {skeletonBar("w-[260px]", "h-[92px]")}
                            </div>
                        </div>
                    </div>
                    {/* Selection panel */}
                    <div className="border border-teal-t30 rounded-lg bg-white p-5">
                        {["w-48", "w-64", "w-56"].map((w, i) => <div key={i} className="mb-3">{skeletonBar(w)}</div>)}
                    </div>
                </div>
            ) : effectiveSubpage === "sources" ? (
                <div className="mt-8 max-w-list-table mx-auto">
                    {skeletonBar("w-96", "h-4 mb-6")}
                    {/* Filter bar */}
                    <div className="flex flex-row gap-3 mb-3">
                        <div className="flex flex-col gap-1">
                            {skeletonBar("w-12", "h-3 mb-1")}
                            {skeletonBar("w-48", "h-9")}
                        </div>
                        <div className="flex flex-col gap-1">
                            {skeletonBar("w-20", "h-3 mb-1")}
                            {skeletonBar("w-48", "h-9")}
                        </div>
                    </div>
                    {skeletonBar("w-32", "h-4 mb-3")}
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-unlisted-neutral-gray-100">
                                {["w-40", "w-48", "w-32"].map((w, i) => (
                                    <th key={i} className="pb-3 pr-6 text-left">
                                        <div className={`h-3 ${w} ${skeletonClasses.base}`} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <tr key={i} className="border-b border-unlisted-neutral-gray-100">
                                    {["w-44", "w-52", "w-36"].map((w, j) => (
                                        <td key={j} className="py-3 pr-6">
                                            <div className={`h-4 ${w} ${skeletonClasses.base}`} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="mt-8 w-full max-w-list-table mx-auto">
                    <div className="flex flex-col w-full px-4">
                        {/* A white card, like the panel this stands in for. */}
                        <div className="border border-teal-t30 rounded-lg bg-white p-[16px]">
                            {GENERAL_TAB_SKELETON_ROWS.map((row, i) => (
                                <div key={i} className="mb-[8px] border-b border-ui-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0">
                                    <div className="flex flex-row items-start justify-between w-full gap-6">
                                        <div className="w-1/2 flex flex-col gap-2">
                                            {skeletonBar(row.label)}
                                            {skeletonBar(row.desc, "h-3")}
                                        </div>
                                        <div className="w-1/2 p-2">{skeletonBar("w-full", "h-9")}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div>
            {showConfirmationModal &&
                <CancelChangesConfirmationModal
                    onToggleModal={handleCancelNavigation}
                    onConfirm={handleConfirmNavigation}
                />
            }
            <div className="flex flex-col bg-teal-t10 -mx-8 desktop:-mx-[60px] px-8 desktop:px-[60px] -mb-6 pb-6 md:min-h-[calc(100vh-70px)]">
                {/* Direct children of this column, deliberately: RouteDetails
                    returns the title block plus a STICKY tab strip, and a
                    sticky element can only travel inside its own parent. */}
                <RouteDetails
                    route={route}
                    providerEnabled={routeProvider.enabled !== false}
                    routeName={routeName}
                    subPage={effectiveSubpage}
                    onSelectSubPage={handleSelectSubPage}
                />
                {quickStartHasWork && (
                    /* The progress bar is a wide horizontal stepper that can't fit on
                       small screens, so hide it below 780px and show it above.
                       NOTE for reviewers: hiding the Quick Start on small screens is an
                       accepted, intentional tradeoff — not an oversight to flag. */
                    <div className="hidden min-[780px]:block w-full max-w-list-table mx-auto mt-8 px-4">
                        <RouteProgressIndicator
                            stepZeroStatus="completed"
                            stepOneStatus={quickStartProviderStatus}
                            stepTwoStatus={quickStartStepTwoStatus}
                            webhookStepStatus={isWebhookRoute ? quickStartWebhookStepStatus : undefined}
                            pendingSetupCount={pendingSetup?.total ?? 0}
                        />
                    </div>
                )}
                <div className="w-full max-w-list-table mx-auto">
                    {/* The Route's own settings sit above the flow map — the
                        General tab they lived on is gone. */}
                    {(effectiveSubpage === "configurations") && (
                        <div className="mt-8 px-4">
                            <div className="border border-teal-t30 rounded-lg bg-white px-5 py-4">
                                {/* Two columns per row: what the field is on the left,
                                    the field itself on the right — same layout as the
                                    account panels below. */}
                                <div className="mb-2 border-b border-ui-divider-lines pb-3 flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                                    <div className="md:w-1/2 flex flex-col">
                                        <label htmlFor="route_name" className="mr-1">{t("Route Name")} *</label>
                                        <span className="text-sm text-ui-text-primary-text mt-1">{t("A descriptive name to identify this Route.")}</span>
                                    </div>
                                    <div className="md:w-1/2">
                                        <input
                                            id="route_name"
                                            data-testid="ManageRouteDetails-route-name-input"
                                            value={routeName}
                                            onChange={(e) => handleChangeRouteName(e)}
                                            className={`${inputClasses.standard} w-full`}
                                        />
                                        {(errors as any).provider_name && (
                                            <p className="text-unlisted-orange-600 text-sm mt-1">{(errors as any).provider_name}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                                    <div className="md:w-1/2 flex flex-col">
                                        <label htmlFor="route_workspace" className="mr-1">{t("Workspace")} *</label>
                                        <span className="text-sm text-ui-text-primary-text mt-1">{t("The workspace this Route belongs to.")}</span>
                                    </div>
                                    <div className="md:w-1/2">
                                        <SelectInput
                                            inputId="route_workspace"
                                            options={organizations.map((org: Organization) => ({ label: org.name, value: org.id }))}
                                            className={"basic-single"}
                                            value={{ label: routeProvider?.owner?.name ?? route?.owner?.name ?? '', value: routeProvider?.owner?.id ?? route?.owner?.id ?? '' }}
                                            isDisabled={false}
                                            isLoading={false}
                                            isClearable={false}
                                            isSearchable={true}
                                            isMulti={false}
                                            name={t("Workspace")}
                                            onChange={(e: any) => handleChangeRouteOwner(e)}
                                            onMenuScrollToBottom={handleLoadMoreOrgs}
                                            handleInputChange={handleSearchOrgDropdown}
                                        />
                                    </div>
                                </div>
                                {/* Also while an error is showing: hiding the row
                                    would strand an invalid draft with no way to
                                    save the correction or cancel out of it. */}
                                {(changesMade || Object.keys(errors || {}).length > 0) && (
                                    <div className="flex items-center gap-3 mt-4">
                                        <button className={buttonClasses.primary} onClick={handleUpdateRoute}
                                            data-testid="ManageRouteDetails-save-general-button">
                                            {t("Save")}
                                        </button>
                                        <button className={buttonClasses.cancel} onClick={() => {
                                            setRouteName(routeDisplayName(route));
                                            setErrors({});
                                            requestRouteReload();
                                            setChangesMade(false);
                                        }}>
                                            {t("Cancel")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {(effectiveSubpage === "configurations") && <ConfigurationsTab
                        route={route}
                        routeProvider={routeProvider}
                        token={token && typeof token === 'string' && !keycloak.isTokenExpired() ? token : undefined}
                        currentMemberIsAdmin={currentMemberIsAdmin}
                        onTestAction={handleTestAction}
                        testActionLoading={testActionLoading}
                        testActionResponse={testActionData}
                        onSaveWebhook={handleUpdateWebhookConfiguration}
                        onCancelWebhook={handleCancelRouteChanges}
                        webhookChangesMade={changesMade}
                        onWebhookChangesMade={() => setChangesMade(true)}
                        reverseRoutesBySite={reverseRoutesBySite}
                        providerCanReceive={!!routeProvider.type?.actions?.some((a: any) => a.type === "push")}
                        destinationsWithRules={destinationsWithRules}
                        reverseDestinationsWithRules={reverseDestinationsWithRules}
                        getRule={(destId: string) => getRuleForDestination(routeConfigData, routeProvider?.id ?? "", destId)}
                        // The reverse flow's rule lives on its own route record, and
                        // its provider is the site — the roles are swapped.
                        getReverseRule={(destId: string) => getRuleForDestination(
                            reverseConfigBySite[destId]?.data, destId, routeProvider?.id ?? "")}
                        reverseRuleEditable={(destId: string) => !!reverseConfigBySite[destId]?.routeId}
                        providerSharedWith={providerSharedWith}
                        providerAlsoProvidesFor={providerAlsoProvidesFor}
                        onEnableReverseFlow={handleEnableReverseFlow}
                        onDisableReverseFlow={handleDisableReverseFlow}
                        onOpenRules={handleOpenRules}
                        onRemoveSite={async (d: any) => { await handleRemoveDestination(d); requestRouteReload(); }}
                        onAddDestination={openAddDestinationDrawer}
                        onReloadRoute={requestRouteReload}
                        onNeedsSetupChange={setPendingSetup}
                        onToggleRouteEnabled={handleToggleProviderEnabled}
                        routeUrlId={route_id}
                        organizations={organizations}
                        onLoadMoreOrgs={handleLoadMoreOrgs}
                        onSearchOrgs={handleSearchOrgDropdown}
                        highlightAddDestination={!!(location.state as any)?.highlightAddDestination}
                    />}
                    {(effectiveSubpage === "sources") && <RouteDetailsSource
                        sources={sources}
                        totalSources={totalSources}
                        globalSearchStr={sourceFilter.search}
                        take={page_size}
                        offset={sourcesOffset}
                        onPageClicked={handleSourcePageClick}
                        pageLoaded={pageLoaded}
                        loadingSources={loadingSources}
                        destinationUrls={destinationUrls}
                        loadingDestinationUrls={loadingDestinationUrls}
                        destinationUrlFilterValue={toSingleSourceValue(sourceFilter.destination_url__in)}
                        onChangeGlobalSourceSearch={handleChangeGlobalSourceSearch}
                        onRefresh={requestSourcesReload}
                        onChangeDestinationUrlFilter={handleSelectSourceUrlFilter}
                        onViewDestination={(destinationId: string) =>
                            navigate(`/routes/${route_id}`, { replace: true, state: { flowSelection: { kind: "account", integrationId: destinationId } } })}
                        ordering={sourceFilter.ordering}
                        onChangeSortBy={handleSortSources}
                    />}
                    {(effectiveSubpage === "logs") && <RouteDetailsLog
                        logs={logs}
                        filters={logsFilter.log_level
                            .concat(logsFilter.log_type, logsFilter.from_date, logsFilter.to_date)}
                        globalSearchStr={logsFilter.search}
                        reloadLogs={loadingLogs}
                        isAppendingLogs={isAppendingLogs}
                        prevCursor={prevLogsCursor}
                        nextCursor={nextLogsCursor}
                        selectedTimezone={selectedTimezone}
                        onSelectTimezone={handleSelectTimezone}
                        onNextPageClicked={handleNextLogPageClick}
                        onPrevPageClicked={handlePrevLogPageClick}
                        requestSort={handleSortLogTimestamp}
                        pageLoaded={pageLoaded}
                        onChangeLogGlobalSearch={handleGlobalLogSearch}
                        onChangeLogStatus={handleFilterLogStatus}
                        onChangeLogType={handleFilterLogType}
                        onChangeLogDate={handleFilterLogsByDate}
                        onResetLogFilters={handleResetLogFilters}
                        onSelectLog={handleSelectLog}
                        onRefreshLogs={handleRefreshLogs}
                        logsError={logsError}
                        // Unhealthy Route (the flow map's red state): logs open
                        // pre-filtered to Warning minimum so the signal is on top.
                        defaultMinimumWarning={route.status === "unhealthy"}
                        hasActiveLogLevel={logsFilter.log_level.length > 0}
                        activeLogLevel={logsFilter.log_level[0] ?? null}
                    />}
                </div>
            </div>
            <AddDestinationDrawer
                isOpen={addDestinationDrawerOpen}
                onClose={() => setAddDestinationDrawerOpen(false)}
                // Now that provider-side records are listed as destinations, the
                // Route's own provider can turn up here — attaching it would
                // point the Route at itself.
                candidates={existingDestPhase0.filter((d: Destination) => d.id !== routeProvider.id)}
                loading={updatingDestinations}
                onAttach={handleAttachDestinationsFromDrawer}
                onCreateNew={() => { setAddDestinationDrawerOpen(false); handleCreateDestination(); }}
                onSearch={(searchStr: string) => {
                    setDestinationFilter((prev: FilterTypes) => ({ ...prev, search: searchStr }));
                    setUpdatingDestinations(true);
                    requestDestinationsReload();
                }}
                totalCount={destinationsTotalCount}
                pageOffset={destinationsPageOffset}
                onPageChange={handleDestinationsPageClick}
                workspaceOptions={organizations.filter((o: Organization) => o.id).map((o: Organization) => ({ label: o.name, value: o.id as string }))}
                workspaceFilter={drawerWorkspaceFilter}
                onChangeWorkspaceFilter={handleDrawerWorkspaceChange}
                loadingWorkspaces={loadingOrganizations}
            />
            <TransformationRulesPanel
                isOpen={rulesPanelOpen}
                onClose={handleCloseRules}
                destination={rulesTarget?.counterpart ?? null}
                providerId={rulesTarget?.providerId ?? ""}
                currentRule={currentPanelRule}
                currentConfigurationName={rulesTarget?.configName ?? ""}
                onSave={handleSaveRules}
                onDelete={handleDeleteRule}
            />
        </div>
    )
}